import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn, UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { saveSession } from "@/lib/auth";
import { LogoMark } from "@/lib/Logo";
import { BRAND } from "@/lib/constants";
import { useSeo } from "@/lib/seo";

export default function Login() {
  const [tab, setTab] = useState("login");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useSeo("Guest Login | Delhi Airport Stay & Transit Services", "Sign in or create your guest account to send and track room enquiries near IGI Airport, Delhi.");

  const go = (user) => navigate(user.role === "guest" ? "/account" : "/admin");

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      saveSession(data.token, data.user);
      go(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", regForm);
      saveSession(data.token, data.user);
      go(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="login-card" data-testid="guest-auth-card">
        <LogoMark size={44} />
        <h1>{BRAND}</h1>
        <p className="login-sub">Guest account · track your enquiries</p>
        <div className="tabs" data-testid="auth-tabs">
          <button className={tab === "login" ? "tab-btn active" : "tab-btn"} onClick={() => { setTab("login"); setError(""); }} data-testid="tab-login">
            Sign in
          </button>
          <button className={tab === "register" ? "tab-btn active" : "tab-btn"} onClick={() => { setTab("register"); setError(""); }} data-testid="tab-register">
            Create account
          </button>
        </div>

        {tab === "login" ? (
          <form onSubmit={submitLogin} className="auth-form" data-testid="guest-login-form">
            <label>
              Email
              <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="you@example.com" data-testid="guest-login-email-input" />
            </label>
            <label>
              Password
              <input type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" data-testid="guest-login-password-input" />
            </label>
            {error && <div className="login-error" data-testid="guest-auth-error">{error}</div>}
            <button className="button button-gold" type="submit" disabled={loading} data-testid="guest-login-submit-button">
              <LogIn size={16} /> {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitRegister} className="auth-form" data-testid="guest-register-form">
            <label>
              Full name
              <input required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} placeholder="Your full name" data-testid="guest-register-name-input" />
            </label>
            <label>
              Email
              <input type="email" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} placeholder="you@example.com" data-testid="guest-register-email-input" />
            </label>
            <label>
              Mobile / WhatsApp
              <input type="tel" required value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} placeholder="10-digit number" data-testid="guest-register-phone-input" />
            </label>
            <label>
              Password (min 8 characters)
              <input type="password" required minLength={8} value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} placeholder="••••••••" data-testid="guest-register-password-input" />
            </label>
            {error && <div className="login-error" data-testid="guest-auth-error">{error}</div>}
            <button className="button button-gold" type="submit" disabled={loading} data-testid="guest-register-submit-button">
              <UserPlus size={16} /> {loading ? "Creating…" : "Create account"}
            </button>
          </form>
        )}

        <Link to="/admin/login" className="login-back" data-testid="owner-login-link">
          Owner or staff? Sign in here
        </Link>
        <Link to="/" className="login-back" data-testid="back-to-site-link">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
